import RestTrackingController from '../../../hooks/RestTrackingController'
import { useEffect } from 'react'
import Swal from 'sweetalert2'
import { SweetAlertIcon } from '../../../utils/Constants'
import { useTranslation } from 'react-i18next'
import { parseStringToArray } from '../../../utils/Utils'
import dayjs from "dayjs/dayjs.min"
import { useSearchParams } from 'react-router-dom'

const TrackingKeywordsController = (model) => {
  const { t } = useTranslation()

  // hooks
  const { rest } = RestTrackingController();
  const [searchParams] = useSearchParams();

  // Load projects
  useEffect(() => {
    handleSearchProjects(null)
  }, [])

  // If arrived from redirection force first search
  useEffect(() => {
    if(searchParams.size>0){
      const id = searchParams.get('q')
      if (id !== undefined) {
        model.setProjectSelected(model.projects.find(p => p.id === id))
      }
    }
  }, [model.projects, model.projectSelected, searchParams]);


  // Set the os when device changed
  useEffect(() => {
    model.setOs(model.osOptions[model.device][0]);
  }, [model.device]);

  useEffect(() => {
    if(!model.projectSelected){
      return
    }
    handleGetKeywordsFromProject(model.projectSelected.id)
    handleGetSov(model.projectSelected)
    handleGetRankRanges(model.projectSelected)
  }, [model.projectSelected, model.filter, model.sortModel, model.paginationModel])


  const handleGetKeywordsFromProject = (id) => {
    const params = { mode: model.modeFilter, projectId: id };

    params['page'] = model.paginationModel.page
    params['pageSize'] = model.paginationModel.pageSize

    if(model.sortModel.length===1){
      params['field'] = model.sortModel[0].field
      params['sort'] = model.sortModel[0].sort
    }

    /*
    model.filter.forEach((value, key) => {
      params[key] = value;
    });*/

    model.setIsLoading((prev) => ({ ...prev, search: true }));
    const getAllProjects = rest({ params, endpoint: 'getTrackingKeywords'})
      .then((data) => {
        model.setTotalKeywords(data.total);
        let isAiOverview = false
        const keywords= data.keywords?.map((item) => {

          if(!item.history) {
            return {
              keyword: item
            };
          }

          // Check if we have ai overviews
          if(item.history.length>1 && item.history[item.history.length - 1].rankAiOverview !== -1 ) {
            isAiOverview = true
          }

          const historySorted = [...item.history].sort(
            (a, b) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf()
          );

          const keywordToday = historySorted[0]?.rankGroup
          const keywordYesterday = historySorted.length>1 ? historySorted[1]?.rankGroup:'-1';
          const keywordBestPosition = Math.min(...item.history.map((h) => h.rankGroup));
          const keywordFirstTime = historySorted.at(-1)?.rankGroup ?? null;

          return {
            keyword: item,
            keywordToday: keywordToday,
            trackingKeywordYesterday: keywordYesterday,
            keywordBestPosition: keywordBestPosition,
            keywordFirstTime: keywordFirstTime,
            keywordUrl: historySorted[0]?.url,
            language: item.language,
            device: item.device,
            os: item.os
          };
        });
        model.setKeywords(keywords)
        model.setIsAiOverview(isAiOverview)
        if(isAiOverview) {
          model.setShowAiOverview(isAiOverview)
        }

      })
      .catch((error) => {
        console.error(`Error:`, error);
      })
      .finally(() => {
        model.setIsLoading((prev) => ({ ...prev, search: false }))
      })
  }


  // functions
const handleSearchProjects = (projectUrlToKeepSelected) => {
  const params = {};
  
  model.setIsLoading((prev) => ({ ...prev, search: true }));
  const getAllProjects = rest({ params, endpoint: 'getTrackingProjects'})
    .then((data) => {
      model.setProjects(data.projects);
      model.setProjectsLoaded(true);
      model.setLimitProjects(data.projectsMax)
      model.setLimitKeywords(data.keywordsMax)

      // If there is a project selected
      if (projectUrlToKeepSelected && data.projects) {
        const projectToSelect = data.projects.find(p => p.url === projectUrlToKeepSelected);
        if (projectToSelect) {
          model.setProjectSelected(projectToSelect);
        }
      }

    })
    .catch((error) => {
      console.error(`Error:`, error);
    })
    .finally(() => {
      model.setIsLoading((prev) => ({ ...prev, search: false }))
    })
}

  const handleAddKeywordsClick = () => {
    const params = {}
    params.projectId = model.projectSelected.id
    params.keywords = []

    const keywordList = parseStringToArray(model.inputNewKeywords)
    const searchEngine = model.projectSelected.url.includes('youtube')?'youtube':model.searchEngine

    keywordList.forEach((keyword) => {
      params.keywords.push(
        {
          se: searchEngine,
          value: keyword,
          country: model.country.country,
          language: model.country.language,
          device: model.device,
          os: model.os
        }
      )
    })

    model.setIsLoading((prev) => ({ ...prev, keywords: true }));
    const addKeyword = rest({ params, endpoint: 'addTrackingKeywords'})
      .then((data) => {
        handleSearchProjects(model.projectSelected.url)
      })
      .catch((error) => {
        if(error.status===429){ // limit reached
          Swal.fire({
            position: "top-end",
            text: t('errorLimitReached'),
            icon: SweetAlertIcon.ERROR,
            showConfirmButton: false,
            timer: 2000,
            toast: true,
            timerProgressBar: true,
            showCloseButton: true
          });
        } else {
          console.error(`Error:`, error)
        }
      })
      .finally(() => {
        model.setIsLoading((prev) => ({ ...prev, keywords: false }))
      })
  }

  const handleOnDeleteClick = (row) => {
    const keywords = [model.keywords.find(k => k.keyword.id === row.keyword.id).keyword]
    deleteKeywords(keywords)
  }

  const handleOnDeleteSelection = () => {
    const keywords = model.selectionModel.map((id) => model.keywords.find(k => k.keyword.id === id).keyword)
    deleteKeywords(keywords)
    model.setSelectionModel([])
  }

  const deleteKeywords = (keywords) => {
    const params = {
      projectId: model.projectSelected.id,
      keywords: keywords.map((item) => ({
        se: item.se,
        value: item.keyword,
        country: item.country,
        language: item.language,
        device: item.device,
        os: item.os
      }))
    }

    model.setIsLoading((prev) => ({ ...prev, keywords: true }));
    const deleteKeywords = rest({ params, endpoint: 'delTrackingKeywords'})
      .then((data) => {
        handleSearchProjects(model.projectSelected.url)
      })
      .catch((error) => {
        console.error(`Error:`, error);
      })
      .finally(() => {
        model.setIsLoading((prev) => ({ ...prev, keywords: false }))
      })
  }

  const handleGetSov = (project) => {
    const params = {}
    params.projectId = project.id

    model.setIsLoading((prev) => ({ ...prev, search: true }));
    const getAllProjects = rest({ params, endpoint: 'getSov'})
      .then((data) => {
        model.setSov(data.sov);
      })
      .catch((error) => {
        console.error(`Error:`, error);
      })
      .finally(() => {
        model.setIsLoading((prev) => ({ ...prev, search: false }))
      })
  }

  const handleGetRankRanges = (project) => {
    const params = {}
    params.projectId = project.id

    model.setIsLoading((prev) => ({ ...prev, search: true }));
    const getAllProjects = rest({ params, endpoint: 'getRankRanges'})
      .then((data) => {
        model.setRanges(data);
      })
      .catch((error) => {
        console.error(`Error:`, error);
      })
      .finally(() => {
        model.setIsLoading((prev) => ({ ...prev, search: false }))
      })
  }

  const handleGetSerps = (row) => { // id es el keywordId
    const params = { keywordId: row.keyword.id }

    if (!row.keyword.history) {
      return
    }

    model.setIsLoading((prev) => ({ ...prev, search: true }))
    rest({ params, endpoint: 'getSerps' })
      .then((data) => {

        // group serps by historyId
        const serpsGroupedByHistoryId = (data.serps || []).reduce((acc, serp) => {
          const { keywordHistoryId } = serp;
          if (!acc[keywordHistoryId]) {
            acc[keywordHistoryId] = [];
          }
          acc[keywordHistoryId].push(serp);
          return acc;
        }, {});

        // Add serps to his related history day
        row.keyword.history.forEach(historyEntry => {
          historyEntry.serps = serpsGroupedByHistoryId[historyEntry.id] || []
        })

      })
      .catch((error) => {
        console.error('Error getting SERPs:', error);
      })
      .finally(() => {
        model.setIsLoading((prev) => ({ ...prev, search: false }))
      })
  };


  const handleCopyKeywords = () => {
    const selectedRows = model.keywords.filter((row: any) =>
      model.selectionModel.includes(row.keyword.id)
    );

    const keywordsToCopy = selectedRows.map((row: any) => row.keyword.keyword).join('\n');
    navigator.clipboard.writeText(keywordsToCopy)
      .then(() => {
        Swal.fire({
          position: "top-end",
          text: "Copiadas: " + selectedRows.length,
          icon: SweetAlertIcon.SUCCESS,
          showConfirmButton: false,
          timer: 2000,
          toast: true,
          timerProgressBar: true,
          showCloseButton: true
        });
      })
  };



  return {
    handleAddKeywordsClick,
    handleOnDeleteClick, handleOnDeleteSelection,
    handleGetSerps,
    handleCopyKeywords
  }
}

export default TrackingKeywordsController;